"use client";

import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null, 'success', 'error'

  // Format a human-readable timestamp
  const getFormattedTimestamp = () => {
    const now = new Date();
    return now.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric'
    });
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const scriptUrl = "https://script.google.com/macros/s/AKfycbzkO9_H6USThcg8OmvAaBzJxxPmiZwF-EpEUeZ9mVlh_pT9SaJvn-pV0IDnAy9FjnBn/exec";

    try {
      const timestamp = getFormattedTimestamp();
      const payload = { 
        email: email, 
        timestamp: timestamp 
      };

      const body = new URLSearchParams();
      body.append('data', JSON.stringify(payload));

      await fetch(scriptUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
        mode: 'no-cors',
      });

      // Set success state
      setSubmitStatus("success");
      
      // Clear email input
      setEmail("");
    } catch (error) {
      console.error("Submit error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
      
      // Reset status message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    }
  };

  return (
    <footer className="w-full bg-[#042626] text-white py-16">
      {/* Main Footer Content */}
      <div className="w-[90%] md:w-[80%] mx-auto">
        {/* Top Section with Logo, Social, and Newsletter */}
        <div className="flex flex-col md:flex-row gap-10 md:gap-20 mb-12">
          {/* Logo and Social Links */}
          <div className="md:w-2/5">
            <div className="mb-8">
              <Image
                src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/footgs.svg"
                alt="GrowthSchool"
                width={280}
                height={50}
                className="h-12 md:h-18 w-auto"
              />
            </div>

            <div className="flex space-x-6 mb-8">
              <a
                href="https://www.facebook.com/growthschoolio"
                className="transition hover:opacity-80"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/Facebook.svg"
                  alt="Facebook"
                  width={24}
                  height={24}
                  className="w-10 h-10"
                />
              </a>
              <a
                href="https://x.com/growthschoolio"
                className="transition hover:opacity-80"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/Twitter.svg"
                  alt="Twitter"
                  width={24}
                  height={24}
                  className="w-10 h-10"
                />
              </a>
              <a
                href="https://www.linkedin.com/school/growthschoolio/"
                className="transition hover:opacity-80"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/LinkedIn.svg"
                  alt="LinkedIn"
                  width={24}
                  height={24}
                  className="w-10 h-10"
                />
              </a>
            </div>
          </div>

          {/* Newsletter Section - Improved layout with status messages */}
          <div className="md:w-3/5 md:max-w-md ml-auto">
            <h3 className="text-xl font-medium mb-2">Best Kept Secrets ✨</h3>
            <p className="text-gray-300 mb-4 text-sm">
              Be the first to find out about the newest programs, workshops, and
              community activities - plus industry news, articles, and special
              reports.
            </p>
            <form
              onSubmit={handleSubscribe}
              className="flex flex-col sm:flex-row gap-3"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-grow px-4 py-3 rounded-lg bg-[#0B3837] border border-[#1A4544] text-white focus:outline-none focus:ring-1 focus:ring-[#33c375]"
                required
                disabled={isSubmitting}
              />
              <button
                type="submit"
                className={`px-6 py-3 bg-[#33c375] text-[#042626] font-medium rounded-lg transition-colors whitespace-nowrap ${
                  isSubmitting
                    ? "opacity-70 cursor-not-allowed"
                    : "hover:bg-[#2ba764]"
                }`}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Subscribing..." : "Subscribe"}
              </button>
            </form>

            {/* Status messages */}
            {submitStatus === "success" && (
              <p className="mt-2 text-[#33c375] text-sm">
                Thank you for subscribing!
              </p>
            )}
            {submitStatus === "error" && (
              <p className="mt-2 text-red-400 text-sm">
                Failed to subscribe. Please try again later.
              </p>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="w-full h-px bg-[#1A4544] my-10"></div>

        {/* Footer Links Columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-14">
          {/* Marketing Column */}
          <div>
            <h4 className="text-lg font-medium mb-5">Mentorship Programs</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  href="/digital-marketing-mentorship-program"
                  className="text-gray-300 hover:text-white transition"
                >
                  Digital Marketing
                </Link>
              </li>
              <li>
                <Link
                  href="/performance-marketing-mentorship-program"
                  className="text-gray-300 hover:text-white transition"
                >
                  Performance Marketing
                </Link>
              </li>
              <li>
                <Link
                  href="/ui-ux-design-mentorship-program"
                  className="text-gray-300 hover:text-white transition"
                >
                  UI UX Design
                </Link>
              </li>
              <li>
                <Link
                  href="/product-management-mentorship-program"
                  className="text-gray-300 hover:text-white transition"
                >
                  Product Management
                </Link>
              </li>
              <li>
                <Link
                  href="/management-consulting-mentorship-program"
                  className="text-gray-300 hover:text-white transition"
                >
                  Management Consulting
                </Link>
              </li>
              <li>
                <Link
                  href="/product-marketing-mentorship-program"
                  className="text-gray-300 hover:text-white transition"
                >
                  Product Marketing
                </Link>
              </li>
            </ul>
          </div>

          {/* Design Column */}
          <div>
            <h4 className="text-lg font-medium mb-5">Workshops</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  href="/workshops/chatgpt-workshop"
                  className="text-gray-300 hover:text-white transition"
                >
                  ChatGPT
                </Link>
              </li>
              <li>
                <Link
                  href="/workshops/linkedin-growth"
                  className="text-gray-300 hover:text-white transition"
                >
                  Linkedin Growth
                </Link>
              </li>
              <li>
                <Link
                  href="/workshops/ai-for-consultants"
                  className="text-gray-300 hover:text-white transition"
                >
                  AI For Consultants
                </Link>
              </li>
              <li>
                <Link
                  href="/workshops/product-management"
                  className="text-gray-300 hover:text-white transition"
                >
                  Product Management
                </Link>
              </li>
              <li>
                <Link
                  href="/workshops/marketing-mastermind"
                  className="text-gray-300 hover:text-white transition"
                >
                  Marketing Mastermind
                </Link>
              </li>
              <li>
                <Link
                  href="/workshops/business-mastermind"
                  className="text-gray-300 hover:text-white transition"
                >
                  Business Mastermind
                </Link>
              </li>
            </ul>
          </div>

          {/* Product Column */}
          <div>
            <h4 className="text-lg font-medium mb-5">Company</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  href="/about-us"
                  className="text-gray-300 hover:text-white transition"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  href="/contact-us"
                  className="text-gray-300 hover:text-white transition"
                >
                  Contact Us
                </Link>
              </li>
              <li>
                <Link
                  href="https://home.growthschool.io/careers"
                  className="text-gray-300 hover:text-white transition"
                >
                  Careers
                </Link>
              </li>
              <li>
                <Link
                  href="https://growthschool.freshdesk.com/support/home"
                  className="text-gray-300 hover:text-white transition"
                >
                  Help
                </Link>
              </li>
            </ul>
          </div>

          {/* Business Column */}
          <div>
            <h4 className="text-lg font-medium mb-5">Engage</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  href="/b2b"
                  className="text-gray-300 hover:text-white transition"
                >
                  Upskill Your Team
                </Link>
              </li>
              <li>
                <Link
                  href="https://forms.growthschool.io/custom/apply-as-a-mentor-at-growthschool/8125ff18-d4a1-42d1-a6f6-0f3bcd3c840a"
                  className="text-gray-300 hover:text-white transition"
                >
                  Apply as Mentor
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Section with Copyright and Legal Links */}
        <div className="w-full h-px bg-[#1A4544] mb-6"></div>
        <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-400 gap-4">
          <div>© 2025 Sisinty Pvt. Ltd. All rights reserved</div>
          <div className="flex flex-wrap gap-4 md:gap-6">
            <Link
              href="/privacy-policy"
              className="hover:text-white transition"
            >
              Privacy Policy
            </Link>
            <Link href="/refund-policy" className="hover:text-white transition">
              Refund Policy
            </Link>
            <Link
              href="/terms-and-conditions"
              className="hover:text-white transition"
            >
              Terms & Conditions
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
